import { inject, Injectable } from '@angular/core';
import { BooleanOperator, ComparisonOperator } from '@common/enums';
import { SiteDriveOdataItem } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SiteItemsTreeOdataService } from '@common/services';
import { CustomTreeElement, SiteItemTreeElement } from '@common/types';
import { createTreeFromSiteItem, getOdataOrderBy, updateTotalChildren } from '@common/utils';
import { SortEvent, TreeComponent, TreeElement } from 'mbs-ui-kit';
import { defer, Observable } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';

@Injectable()
export class SharePointTreeHelperService {
  private readonly treeService = inject(SiteItemsTreeOdataService);

  requestHandlerTree(
    treeElem: SiteItemTreeElement,
    update: boolean,
    setLoadingFn: (val: boolean) => void
  ): (source: Observable<ODataPagedResult<SiteDriveOdataItem>>) => Observable<SiteItemTreeElement[]> {
    return (source: Observable<ODataPagedResult<SiteDriveOdataItem>>): Observable<SiteItemTreeElement[]> => {
      return defer(() => {
        setLoadingFn(true);

        return source.pipe(
          take(1),
          map(({ data, count }) => {
            return {
              node: createTreeFromSiteItem(data),
              count
            };
          }),
          updateTotalChildren<SiteItemTreeElement>(treeElem, update),
          finalize(() => setLoadingFn(false))
        );
      });
    };
  }

  getPage(treeElem: SiteItemTreeElement, reset?: boolean): number {
    return reset ? 1 : this.getTreeElemLoadedPage(treeElem);
  }

  private getTreeElemLoadedPage(treeElem: TreeElement): number {
    const pageSize = this.treeService.pageSize;
    const loadedCount = treeElem.children?.length || 0;
    const totalChildren = treeElem.totalChildren ?? 0;

    if (!pageSize || !totalChildren || !loadedCount || pageSize > totalChildren) return 1;

    return Math.floor(loadedCount / pageSize) + 1;
  }

  getFilter(config?: { path?: string; isLastDriveItem?: boolean }): string {
    // const neSiteItemStr = `TreeItemType ${ComparisonOperator.Ne} '${TreeItemTypes.SiteItem}'`;
    // const neSiteBlockStr = `TreeItemType ${ComparisonOperator.Ne} '${TreeItemTypes.SiteBlock}'`;
    // return isListItems ? `${odataTypeFilter}` : `${neSiteItemStr} ${BooleanOperator.And} ${neSiteBlockStr} ${BooleanOperator.And} ${odataTypeFilter}`;
    const { path, isLastDriveItem } = config || {};
    const odataTypeFilter = `Type ${ComparisonOperator.Eq} false`;
    const odataIdFilter = this.getOdataIdFilter(path) || '';

    return isLastDriveItem ? `${odataIdFilter} ${BooleanOperator.And} ${odataTypeFilter}` : odataTypeFilter;
  }

  getOdataIdFilter(path: string): string {
    return path ? `Id ${ComparisonOperator.Eq} ${path}` : null;
  }

  getOrder(orderBy: SortEvent, constSortProp: string) {
    const { column, direction } = orderBy || {};
    const extra = column === 'Type' ? constSortProp : null;

    return getOdataOrderBy(column, direction, extra);
  }

  findChildByPath(root: CustomTreeElement<any>, path: string): CustomTreeElement<any> {
    return root.children?.find((treeEl) => treeEl.id == path);
  }

  unselectAllTreeItems(treeComponent: TreeComponent) {
    treeComponent.getSelected().forEach((el) => treeComponent.doSelect(false, el));
  }
}
